// Lines, areas and labels on a scene, apart from Konva. Points are flat
// image-pixel arrays, [x0, y0, x1, y1, …], as MapDrawing stores them; a label
// has one point, its centre, and its width is the font size.

import type { LiveDrawing } from "../types/generated/live";
import { turnPoint } from "./transform";

export type DrawStyle = LiveDrawing["style"];
export type DrawSize = "thin" | "medium" | "wide";
export type LabelSize = "small" | "medium" | "large";
// What the server sends, less what a draft has no value for until the answer.
export type Drawing = Omit<LiveDrawing, "z" | "text"> & Partial<Pick<LiveDrawing, "z" | "text">>;
// A label's width at a font size, from the canvas on the board.
export type Measure = (text: string, fontSize: number) => number;
type Box = { x: number; y: number; width: number; height: number };
export type Look = { color: string; width: number; style: DrawStyle };
type Cell = { cell_w: number; cell_h: number };
type Point = { x: number; y: number };
// -1 for the left or top side, 1 for the right or bottom, as the pieces' anchors.
export type Corner = [number, number];
type SnapMath = {
	cellAt: (x: number, y: number) => { col: number; row: number };
	centerOf: (col: number, row: number) => Point;
	cellPolygon: (col: number, row: number) => number[];
};

// Of the shorter cell side, so a river looks the same on any grid.
export const WIDTHS: Record<DrawSize, number> = { thin: 0.04, medium: 0.08, wide: 0.14 };
// Each style belongs to one grid kind, and picking it turns Encaixar na grade
// on or off. Livre is none of them: it takes the panel's own choices.
export const PRESETS: Record<string, { color: string; size: DrawSize; style: DrawStyle; grid: string; snap: boolean }> = {
	rio: { color: "azul", size: "medium", style: "solid", grid: "hex", snap: false },
	estrada: { color: "grafite", size: "thin", style: "solid", grid: "hex", snap: false },
	trilha: { color: "grafite", size: "thin", style: "dotted", grid: "hex", snap: false },
	fronteira: { color: "vermelho", size: "thin", style: "solid", grid: "hex", snap: true },
	muro: { color: "preto", size: "wide", style: "solid", grid: "square", snap: true },
	cerca: { color: "preto", size: "medium", style: "dashed", grid: "square", snap: true },
	desnivel: { color: "preto", size: "thin", style: "ledge", grid: "square", snap: false },
	passagem: { color: "preto", size: "wide", style: "tunnel", grid: "square", snap: false },
	rastro: { color: "grafite", size: "medium", style: "dotted", grid: "square", snap: false },
};

// A ledge's teeth and a tunnel's casing, as shares of the shorter cell side;
// the casing is as wide as the walls' ink, measured on the book maps.
export const WALL_WIDTH = 0.09;
const TOOTH_GAP = 0.3;
const TOOTH_BASE = 0.16;
export const TOOTH_HEIGHT = 0.15;
const TOOTH_FIRST = 0.15;
// What one ledge may be given, whatever its length.
export const MAX_TEETH = 2000;
export const TOOTH_PX_MIN = 2;
// A tunnel's carved core is this many of the drawing's widths wide.
export const TUNNEL_CORE = 3;

// A style with a look of its own, which the Traço group does not offer.
export const isStyled = (style: DrawStyle) => style === "ledge" || style === "tunnel";

export function presetsFor(kind: string | undefined) {
	return Object.keys(PRESETS).filter((key) => PRESETS[key].grid === kind);
}

// The style a scene of this grid kind opens on; hexes when the kind is unknown.
export function defaultPreset(kind: string | undefined) {
	return presetsFor(kind)[0] ?? "rio";
}

export const LABEL_SIZES: Record<LabelSize, number> = { small: 0.25, medium: 0.4, large: 0.6 };
// Of the font size: the light outline that keeps a label legible on any terrain.
export const LABEL_OUTLINE = 0.15;
// Tailwind's font-serif, the site's Alegreya.
export const LABEL_FONT = 'Alegreya, ui-serif, Georgia, Cambria, "Times New Roman", Times, serif';
// The map image is the same in both themes, so the outline stays light.
export const LABEL_STROKE = "#ffffff";
// MapDrawing::WIDTHS and TEXT_WIDTHS.
const MIN_WIDTH = 1;
const MIN_LABEL = 4;
const MAX_WIDTH = 500;
// MapToken::BOUNDS_MARGIN: how far past the scene a point may lie, of its side.
const MARGIN = 0.5;

export function widthFor(size: DrawSize, grid: Cell) {
	return cellShare(WIDTHS[size] ?? WIDTHS.medium, grid, MIN_WIDTH);
}

export function labelSize(size: LabelSize, grid: Cell) {
	return cellShare(LABEL_SIZES[size] ?? LABEL_SIZES.medium, grid, MIN_LABEL);
}

function cellShare(factor: number, grid: Cell, least: number) {
	const value = Math.round(factor * Math.min(grid.cell_w, grid.cell_h) * 100) / 100;
	return Math.min(MAX_WIDTH, Math.max(least, value));
}

// The nearest cell corner or cell centre. It is always one of the cell
// under the point's, on any grid, however stretched.
export function nearestSnap(math: SnapMath, point: Point) {
	const { col, row } = math.cellAt(point.x, point.y);
	const centre = math.centerOf(col, row);
	const targets = [centre.x, centre.y, ...math.cellPolygon(col, row)];
	let best = centre;
	for (let index = 2; index < targets.length; index += 2) {
		const target = { x: targets[index], y: targets[index + 1] };
		if (Math.hypot(target.x - point.x, target.y - point.y) < Math.hypot(best.x - point.x, best.y - point.y)) best = target;
	}
	return best;
}

// The label's box, centred on its point, with its outline.
export function labelBox({ points, width: fontSize, text }: Drawing, measure: Measure): Box {
	const outline = LABEL_OUTLINE * fontSize;
	const width = measure(text ?? "", fontSize) + outline;
	const height = fontSize + outline;
	return { x: points[0] - width / 2, y: points[1] - height / 2, width, height };
}

// How far a line reaches from its points: half its width, a ledge's tooth
// (on either side, since the box does not know which is the right one), or
// half a tunnel's casing. Without the grid there is no cell side to take the
// teeth and the casing from.
export function reach({ style, width }: Pick<Drawing, "style" | "width">, grid?: Cell) {
	const side = grid ? Math.min(grid.cell_w, grid.cell_h) : 0;
	if (style === "ledge") return Math.max(width / 2, TOOTH_HEIGHT * side);
	if (style === "tunnel") return (TUNNEL_CORE * width) / 2 + WALL_WIDTH * side;
	return width / 2;
}

// What a drawing covers: its points widened by its reach, or a label's box.
export function bounds(drawing: Drawing, measure: Measure, grid?: Cell): Box {
	if (drawing.kind === "text") return labelBox(drawing, measure);
	const { points } = drawing;
	const half = reach(drawing, grid);
	const xs = points.filter((_, index) => index % 2 === 0);
	const ys = points.filter((_, index) => index % 2 === 1);
	const x = Math.min(...xs) - half;
	const y = Math.min(...ys) - half;
	return { x, y, width: Math.max(...xs) + half - x, height: Math.max(...ys) + half - y };
}

// A ledge's teeth along the polyline, each a triangle [x1, y1, x2, y2, x3, y3]
// whose base lies on the line and whose point is on the right of the
// direction the points run. They are spaced by the length walked, so a corner
// or a freehand stroke's many points neither bunch nor skip them, and a base
// follows the line round a vertex. A tooth whose base would run past either
// end of the line is left out, and the spacing grows past 0.3 of a side
// where a long line would otherwise take more than MAX_TEETH.
export function teeth(given: number[], side: number) {
	// A repeated vertex is one, so no segment has no length.
	const points = given.slice(0, 2);
	for (let index = 2; index < given.length; index += 2) {
		if (given[index] !== points[points.length - 2] || given[index + 1] !== points[points.length - 1]) points.push(given[index], given[index + 1]);
	}
	const count = points.length / 2;
	const ends = [0];
	for (let index = 1; index < count; index++)
		ends.push(ends[index - 1] + Math.hypot(points[index * 2] - points[index * 2 - 2], points[index * 2 + 1] - points[index * 2 - 1]));
	const total = ends[count - 1] ?? 0;
	const [gap, first, half, height] = [Math.max(TOOTH_GAP * side, total / MAX_TEETH), TOOTH_FIRST * side, (TOOTH_BASE * side) / 2, TOOTH_HEIGHT * side];
	// The point a length along the line, taking the segment it lies on as the one that ends after it.
	const along = (length: number) => {
		let [low, high] = [0, count - 1];
		while (high - low > 1) {
			const middle = (low + high) >> 1;
			if (ends[middle] <= length) low = middle;
			else high = middle;
		}
		const share = (length - ends[low]) / (ends[low + 1] - ends[low]);
		return {
			x: points[low * 2] + share * (points[low * 2 + 2] - points[low * 2]),
			y: points[low * 2 + 1] + share * (points[low * 2 + 3] - points[low * 2 + 1]),
		};
	};
	const found: number[][] = [];
	for (let length = first; length + half <= total; length += gap) {
		if (length - half < 0) continue;
		const [a, b] = [along(length - half), along(length + half)];
		const [mx, my, base] = [(a.x + b.x) / 2, (a.y + b.y) / 2, Math.hypot(b.x - a.x, b.y - a.y)];
		if (!base) continue;
		found.push([a.x, a.y, b.x, b.y, mx - ((b.y - a.y) / base) * height, my + ((b.x - a.x) / base) * height]);
	}
	return found;
}

// The same line the other way round, which puts a ledge's teeth on its other side.
export function flipPoints(points: number[]) {
	const flipped: number[] = [];
	for (let index = points.length / 2 - 1; index >= 0; index--) flipped.push(points[index * 2], points[index * 2 + 1]);
	return flipped;
}

// Rounded as the pen rounds new points, which keeps the payload short.
export function translate(points: number[], dx: number, dy: number) {
	return points.map((value, index) => Math.round((value + (index % 2 ? dy : dx)) * 10) / 10);
}

// The offset that keeps every point on the scene; a drawing already past an
// edge may come back, never go further out.
export function clampShift(points: number[], dx: number, dy: number, { width_px, height_px }: { width_px: number; height_px: number }) {
	const axis = (values: number[], shift: number, most: number) => {
		const low = Math.min(-Math.min(...values), 0);
		const high = Math.max(most - Math.max(...values), 0);
		return Math.min(high, Math.max(low, shift));
	};
	return {
		dx: axis(
			points.filter((_, index) => index % 2 === 0),
			dx,
			width_px,
		),
		dy: axis(
			points.filter((_, index) => index % 2 === 1),
			dy,
			height_px,
		),
	};
}

// Clockwise about the centre, rounded as a move rounds.
export function turnPoints(points: number[], centre: Point, degrees: number) {
	const turned = [];
	for (let index = 0; index < points.length; index += 2) {
		const point = turnPoint(centre, { x: points[index] - centre.x, y: points[index + 1] - centre.y }, degrees);
		turned.push(round(point.x), round(point.y));
	}
	return turned;
}

export function scalePoints(points: number[], anchor: Point, factor: number) {
	return points.map((value, index) => round((index % 2 ? anchor.y : anchor.x) + (value - (index % 2 ? anchor.y : anchor.x)) * factor));
}

// The points' own extent, without the width.
export function pointsBox(points: number[]) {
	const [xs, ys] = axes(points);
	const [left, top] = [Math.min(...xs), Math.min(...ys)];
	return { x: left, y: top, width: Math.max(...xs) - left, height: Math.max(...ys) - top };
}

// The factor a corner drag gives, keeping the opposite corner of the points'
// box where it is: the pointer's share of the diagonal, clamped so the box's
// longer side stays a quarter of the shorter cell side at least.
export function scaleFromCorner(points: number[], [sx, sy]: Corner, pointer: Point, cell: Cell) {
	const box = pointsBox(points);
	const longest = Math.max(box.width, box.height);
	if (!longest) return 1;
	const fixed = { x: sx > 0 ? box.x : box.x + box.width, y: sy > 0 ? box.y : box.y + box.height };
	const diagonal = { x: sx * box.width, y: sy * box.height };
	const share = ((pointer.x - fixed.x) * diagonal.x + (pointer.y - fixed.y) * diagonal.y) / (diagonal.x ** 2 + diagonal.y ** 2);
	return Math.max(share, Math.min(cell.cell_w, cell.cell_h) / 4 / longest);
}

// The opposite corner of the points' box, the anchor a corner drag scales about.
export function oppositeCorner(points: number[], [sx, sy]: Corner): Point {
	const box = pointsBox(points);
	return { x: sx > 0 ? box.x : box.x + box.width, y: sy > 0 ? box.y : box.y + box.height };
}

// A result past the scene's margin is shifted back onto the scene, or null
// when it cannot fit there.
export function fitOnScene(points: number[], scene: { width_px: number; height_px: number }) {
	const [xs, ys] = axes(points);
	const [width, height] = [scene.width_px, scene.height_px];
	const within = (values: number[], most: number, margin: number) => values.every((value) => value >= -margin && value <= most + margin);
	if (within(xs, width, width * MARGIN) && within(ys, height, height * MARGIN)) return points;
	const back = (values: number[], most: number) =>
		Math.min(...values) < 0 ? -Math.min(...values) : Math.max(...values) > most ? most - Math.max(...values) : 0;
	const { dx, dy } = clampShift(points, back(xs, width), back(ys, height), scene);
	const shifted = translate(points, dx, dy);
	const [shiftedX, shiftedY] = axes(shifted);
	return within(shiftedX, width, 0) && within(shiftedY, height, 0) ? shifted : null;
}

// "livre" takes the panel's colour, width and stroke.
export function presetStyle(
	{ preset, color = "azul", size = "medium", style = "solid" }: { preset: string; color?: string; size?: DrawSize; style?: DrawStyle },
	grid: Cell,
): Look {
	const chosen = PRESETS[preset] ?? { color, size, style };
	return { color: chosen.color, width: widthFor(chosen.size, grid), style: chosen.style };
}

// The style of the grid's set whose colour, width and stroke the drawing
// has, else null.
export function styleOf(drawing: Look, grid: Cell & { kind?: string }) {
	const matches = (key: string) => {
		const { color, size, style } = PRESETS[key];
		return color === drawing.color && style === drawing.style && Math.abs(widthFor(size, grid) - drawing.width) < 0.005;
	};
	return presetsFor(grid.kind).find(matches) ?? null;
}

// Round caps turn the near-zero dashes into dots.
export function dashFor(style: DrawStyle, width: number) {
	if (style === "dashed") return [3 * width, 2 * width];
	if (style === "dotted") return [0.01, 2 * width];
	return null;
}

// Ramer–Douglas–Peucker with a stack, since a long freehand stroke would
// recurse too deep.
export function simplify(points: number[], tolerance: number) {
	const count = points.length / 2;
	if (count <= 2) return [...points];
	const keep = new Uint8Array(count);
	keep[0] = 1;
	keep[count - 1] = 1;
	const stack = [[0, count - 1]];
	while (stack.length) {
		const [first, last] = stack.pop();
		let farthest = -1;
		let distance = tolerance;
		for (let index = first + 1; index < last; index++) {
			const next = segmentDistance(at(points, index), at(points, first), at(points, last));
			if (next > distance) {
				distance = next;
				farthest = index;
			}
		}
		if (farthest < 0) continue;
		keep[farthest] = 1;
		stack.push([first, farthest], [farthest, last]);
	}
	const kept = [];
	for (let index = 0; index < count; index++) if (keep[index]) kept.push(points[index * 2], points[index * 2 + 1]);
	return kept;
}

// The order the layer stacks drawings in, bottom first.
export function stackOrder(a: Drawing, b: Drawing) {
	return (a.z ?? 0) - (b.z ?? 0) || String(a.id).localeCompare(String(b.id));
}

// The topmost drawing within its reach plus the tolerance of the point,
// holding it inside for an area, or within its box for a label. Labels need
// the measurer.
export function hitDrawing(drawings: Drawing[], point: Point, tolerance: number, measure?: Measure, grid?: Cell) {
	let hit: Drawing | null = null;
	for (const drawing of drawings) {
		if (hit && stackOrder(drawing, hit) <= 0) continue;
		if (touches(drawing, point, tolerance, measure, grid)) hit = drawing;
	}
	return hit;
}

function touches(drawing: Drawing, point: Point, tolerance: number, measure?: Measure, grid?: Cell) {
	if (drawing.kind === "text") return !!measure && withinBox(labelBox(drawing, measure), point, tolerance);
	const { kind, points } = drawing;
	const count = points.length / 2;
	const area = kind === "area";
	if (area && inside(points, point)) return true;
	const within = reach(drawing, grid) + tolerance;
	const segments = area ? count : count - 1;
	for (let index = 0; index < segments; index++) {
		if (segmentDistance(point, at(points, index), at(points, (index + 1) % count)) <= within) return true;
	}
	return false;
}

function withinBox(box: Box, { x, y }: Point, tolerance: number) {
	return x >= box.x - tolerance && x <= box.x + box.width + tolerance && y >= box.y - tolerance && y <= box.y + box.height + tolerance;
}

function inside(points: number[], { x, y }: Point) {
	let within = false;
	const count = points.length / 2;
	for (let index = 0, previous = count - 1; index < count; previous = index++) {
		const a = at(points, index);
		const b = at(points, previous);
		if (a.y > y !== b.y > y && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) within = !within;
	}
	return within;
}

const axes = (points: number[]) => [points.filter((_, index) => index % 2 === 0), points.filter((_, index) => index % 2 === 1)];

const round = (value: number) => Math.round(value * 10) / 10;

function at(points: number[], index: number): Point {
	return { x: points[index * 2], y: points[index * 2 + 1] };
}

function segmentDistance(point: Point, a: Point, b: Point) {
	const dx = b.x - a.x;
	const dy = b.y - a.y;
	const length = dx * dx + dy * dy;
	const share = length ? Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / length)) : 0;
	return Math.hypot(point.x - (a.x + share * dx), point.y - (a.y + share * dy));
}
